import React, { useState } from 'react';
import { Clock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { AppView } from '../../types';

interface WorkHoursOvertimeCalculatorProps {
  onNavigateToView: (view: AppView) => void;
}

export const WorkHoursOvertimeCalculator: React.FC<WorkHoursOvertimeCalculatorProps> = ({ onNavigateToView }) => {
  const [startTime, setStartTime] = useState<string>('08:30');
  const [endTime, setEndTime] = useState<string>('17:15');
  const [breakMinutes, setBreakMinutes] = useState<string>('45');
  const [hourlyRate, setHourlyRate] = useState<string>('25');
  const [standardWeeklyHours, setStandardWeeklyHours] = useState<string>('40');
  const [actualWeeklyHours, setActualWeeklyHours] = useState<string>('45');
  const [overtimeMultiplier, setOvertimeMultiplier] = useState<string>('1.5');

  // Calculations for daily shift
  // Parse times
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const startTotalMins = (startH || 0) * 60 + (startM || 0);
  const endTotalMins = (endH || 0) * 60 + (endM || 0);
  const breakM = parseFloat(breakMinutes) || 0;

  let diffMins = endTotalMins - startTotalMins;
  if (diffMins < 0) diffMins += 24 * 60; // Handle overnight shifts
  const workedMins = Math.max(0, diffMins - breakM);
  const decimalHours = workedMins / 60;
  const dailyPay = decimalHours * (parseFloat(hourlyRate) || 0);

  // Overtime weekly calculation
  const stdW = parseFloat(standardWeeklyHours) || 40;
  const actW = parseFloat(actualWeeklyHours) || 40;
  const otMult = parseFloat(overtimeMultiplier) || 1.5;
  const rate = parseFloat(hourlyRate) || 0;

  const regularW = Math.min(stdW, actW);
  const overtimeW = Math.max(0, actW - stdW);
  const regularPay = regularW * rate;
  const overtimePay = overtimeW * rate * otMult;
  const totalWeeklyPay = regularPay + overtimePay;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('everyday-command-center')}
              className="p-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-[#0F172A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                YamooPay Labor & Overtime Engine (Prompt 14)
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">
                Calculateur d'Heures de Travail & Heures Supplémentaires
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            Centre de Commande
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            {/* Shift Calculator */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Calculateur de Poste Quotidien</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Heure de Début</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Heure de Fin</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Pause (minutes)</label>
                  <input
                    type="number"
                    value={breakMinutes}
                    onChange={(e) => setBreakMinutes(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex items-center justify-between">
                <div>
                  <div className="text-xs text-[#64748B]">Durée nette du poste</div>
                  <div className="text-xl font-bold text-[#0F172A] mt-0.5">{decimalHours.toFixed(2)} heures décimales</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#64748B]">Rémunération estimée</div>
                  <div className="text-xl font-bold text-blue-600 mt-0.5">${dailyPay.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Overtime Calculator */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Moteur d'Heures Supplémentaires Hebdomadaires</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Taux Horaire ($)</label>
                  <input
                    type="number"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Seuil Normal (h)</label>
                  <input
                    type="number"
                    value={standardWeeklyHours}
                    onChange={(e) => setStandardWeeklyHours(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Heures Travaillées (h)</label>
                  <input
                    type="number"
                    value={actualWeeklyHours}
                    onChange={(e) => setActualWeeklyHours(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Multiplicateur (ex: 1.5)</label>
                  <input
                    type="number"
                    value={overtimeMultiplier}
                    onChange={(e) => setOvertimeMultiplier(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results Summary Card */}
          <div className="bg-gradient-to-br from-sky-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md h-fit">
            <div>
              <div className="text-xs font-bold text-sky-300 uppercase tracking-wider mb-2">Paie Hebdomadaire</div>
              <div className="space-y-4 mt-4">
                <div>
                  <div className="text-xs text-sky-200">Heures régulières ({regularW}h)</div>
                  <div className="text-xl font-bold">${regularPay.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-sky-200">Heures supplémentaires ({overtimeW}h à {otMult}x)</div>
                  <div className="text-xl font-bold text-sky-300">${overtimePay.toFixed(2)}</div>
                </div>
                <div className="pt-4 border-t border-white/10">
                  <div className="text-xs text-sky-200">Salaire Brut Total</div>
                  <div className="text-3xl font-extrabold text-white">${totalWeeklyPay.toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div className="mt-8 pt-4 border-t border-white/10 text-[11px] text-sky-200">
              Estimation générique. L'admissibilité réelle dépend de la législation locale du travail.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
